import { inject, Injectable, signal } from '@angular/core';
import { LocalStorageService } from '../storage/local-storage.service';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import type { User } from '../../models/user.model';
import { BaseService } from './base.service';
import { endpoints } from '../../../../environments/environment.common';
import { LoginCredentialsRequest } from '../../requests/login-credentials.request';
import { RegisterDataRequest } from '../../requests/register-data.request';
import { UserService } from './user.service';
import type { AuthResponse } from '../../responses/auth.response';
import { HttpHeaders } from '@angular/common/http';
import { RestoreDataRequest } from '@/shared/requests/restore-data.request';
import { Role } from '@/shared/models/role.model';
import { RestoreLinkDataRequest } from '@/shared/requests/restore-link-data.request';

@Injectable({
    providedIn: 'root'
})
export class AuthService extends BaseService {
    private readonly router = inject(Router);
    private readonly storage = inject(LocalStorageService);
    private readonly userService = inject(UserService);
    readonly currentUser = this.userService.currentUser;
    private readonly _isAuthenticated = signal(false);

    constructor() {
        super();
        // Verificar si hay un token guardado
        const token = this.storage.getItem('token');
        if (token) {
            this._isAuthenticated.set(true);
            // Cargar datos del usuario
            this.userService.loadUser().subscribe({
                next: () => {},
                // Si al cargar el usuario hay error (sin token o antes de que se inicialice el interceptor)
                error: (err) => {
                    // Si el error es sin token
                    if (err.status === 401) {
                        //pedimos nuevo token
                        this.refreshToken().subscribe({
                            next: (resp) => {
                                const newToken = resp.token;
                                if (newToken) {
                                    //Almacenamos el nuevo token
                                    this.storage.setItem('token', newToken);
                                    //volvemos a cargar el usuario con el nuevo token
                                    this.userService.loadUser().subscribe();
                                }
                            },
                            error: () => {
                                //si no conseguimos nuevo token, cerramos sesión
                                this.logout();
                            }
                        });
                    }
                }
            });
        }
    }

    sanctum(): Observable<void> {
        // Solo obtiene la cookie de CSRF de Sanctum; no intenta cargar usuario ni alterar estado
        return this.get<void>(endpoints.sanctum);
    }

    login(credentials: LoginCredentialsRequest): Observable<AuthResponse> {
        return this.post<AuthResponse>(endpoints.auth.login, credentials).pipe(
            tap((response: AuthResponse) => {
                const token = response.data?.token;
                const user = response.data?.user;

                if (response.data?.requires_2fa && response.data?.user_id) {
                    // Guardar user_id pendiente para el paso 2FA
                    this.storage.setItem('2fa_pending_user_id', String(response.data.user_id));
                    this.storage.setItem('2fa_pending_method', response.data.two_factor_method ?? 'email');
                    return;
                }

                if (token && user) {
                    this.storage.setItem('token', token);
                    this.userService.setUser(user);
                    this._isAuthenticated.set(true);
                }
            })
        );
    }

    get pendingTwoFactorUserId(): string | null {
        return this.storage.getItem('2fa_pending_user_id');
    }

    get pendingTwoFactorMethod(): string {
        return this.storage.getItem('2fa_pending_method') ?? 'email';
    }

    clearPendingTwoFactor(): void {
        this.storage.removeItem('2fa_pending_user_id');
        this.storage.removeItem('2fa_pending_method');
    }

    register(userData: RegisterDataRequest): Observable<AuthResponse> {
        // Registro directo sin CSRF token
        return this.post<AuthResponse>(endpoints.auth.register, userData).pipe(
            tap((response: AuthResponse) => {
                // Guardar el token y usuario del response
                const token = response.data?.token;
                const user = response.data?.user;

                if (token && user) {
                    this.storage.setItem('token', token);
                    this.userService.setUser(user);
                    this._isAuthenticated.set(true);
                }
            })
        );
    }

    logout(): void {
        const token = this.storage.getItem('token');

        // Función para limpiar el estado local
        const clearLocalState = () => {
            this.userService.clearUser();
            this._isAuthenticated.set(false);
            this.storage.removeItem('token');
            this.router.navigate(['/auth/login']).then();
        };

        // Si hay token, intentar hacer logout en el backend
        if (token) {
            this.post(endpoints.auth.logout, {}).subscribe({
                next: () => {
                    clearLocalState();
                },
                error: () => {
                    // Incluso si falla, limpiar el estado local
                    clearLocalState();
                }
            });
        } else {
            // Si no hay token, solo limpiar el estado local
            clearLocalState();
        }
    }

    isAuthenticated(): boolean {
        return this._isAuthenticated();
    }

    verifyMfa(code: string, method: string): Observable<any> {
        const userId = this.pendingTwoFactorUserId;
        return this.post(endpoints.auth.verifyMfa, { user_id: userId, code, method }).pipe(
            tap((response: any) => {
                const token = response.data?.token;
                const user = response.data?.user;
                if (token && user) {
                    this.clearPendingTwoFactor();
                    this.storage.setItem('token', token);
                    this.userService.setUser(user);
                    this._isAuthenticated.set(true);
                }
            })
        );
    }

    sendMfaCodeByEmail(): Observable<any> {
        const userId = this.pendingTwoFactorUserId;
        return this.post(endpoints.auth.sendMfaCodeEmail, { user_id: userId });
    }

    sendMfaCodeBySMS(): Observable<any> {
        const userId = this.pendingTwoFactorUserId;
        return this.post(endpoints.auth.sendMfaCodeSMS, { user_id: userId });
    }

    updateTwoFactorSettings(enabled: boolean, method: 'email' | 'sms'): Observable<any> {
        return this.put(endpoints.auth.twoFactorSettings, { two_factor_enabled: enabled, two_factor_method: method });
    }

    resetPassword(email: string): Observable<any> {
        return this.post(endpoints.auth.resetPassword, { email });
    }

    restorePassword(restoreData: RestoreDataRequest): Observable<any> {
        return this.post(endpoints.auth.restorePassword, restoreData);
    }

    generateRestorePasswordLink(restoreLinkData: RestoreLinkDataRequest): Observable<any> {
        return this.post(endpoints.auth.restorePasswordLink, restoreLinkData);
    }

    generateRestorePasswordLinkEmail(restoreLinkData: RestoreLinkDataRequest): Observable<any> {
        return this.post(endpoints.auth.restorePasswordLinkEmail, restoreLinkData);
    }

    refreshToken(): Observable<any> {
        //Cogemos del localStorage el token viejo para que el back sepa quien está pidiendo el nuevo
        const token = this.storage.getItem('token');

        //configuramos el header para hacer la petición
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
        });

        //llamamos al back para que nos de un nuevo token
        return this.post(
            endpoints.auth.refreshToken,
            {},
            { headers } // headers con el token viejo
        );
    }

    getPrimaryRole(roles?: Role[] | null): string | null {
        if (!roles?.length) return null;

        const codes = roles.map((r) => r.code);

        if (codes.includes('superadmin')) return 'superadmin';
        if (codes.includes('manager')) return 'manager';
        if (codes.includes('csr')) return 'csr';
        if (codes.includes('dispatcher') || codes.includes('dispacher')) return 'dispatcher';
        if (codes.includes('driver')) return 'driver';
        if (codes.includes('client')) return 'client';

        return null;
    }

    getDefaultRouteForRoles(user: User): string[] {
        const role = this.getPrimaryRole(user.roles);
        switch (role) {
            case 'superadmin':
            case 'manager':
                return ['/dashboard'];
            case 'csr':
                return ['/bookings/new'];
            case 'dispatcher':
                return ['/dispatcher-bookings'];
            case 'client':
                return ['/clients', user.client!.id];
            case 'driver':
                return ['/drivers', user.driver!.id];
            default:
                return ['/profile'];
        }
    }

    hasAnyRole(rolesToCheck: string | string[], userRoles: Role[]): boolean {
        if (!userRoles?.length) return false;

        const roles = Array.isArray(rolesToCheck) ? rolesToCheck : [rolesToCheck];

        return roles.some((role) => userRoles.some((userRole) => userRole.code === role));
    }
}
